/**
 * /why-sparrow — a 3-column comparison page.
 *
 * Sparrow vs SPSS (the proprietary incumbent) vs R/Stata (the
 * "powerful but you need to learn it" peer). Rows are real,
 * verifiable claims — not marketing fluff. I checked the ones I
 * wasn't sure of against Sparrow's own source before listing them.
 *
 * The intent isn't "Sparrow wins every row" — that would read as
 * dishonest. Rows where SPSS or R legitimately do something Sparrow
 * doesn't get marked as such, with a short explanation.
 */

import { Link } from "react-router-dom"
import {
  ArrowUpRight, Check, ChevronRight, Minus, X,
} from "lucide-react"
import { SPARROW_URL } from "@/lib/sparrow"
import { Sparrowmark } from "@/brand/Sparrowmark"

type Verdict = "yes" | "no" | "partial"

interface Row {
  label: string
  /** One-line caption shown under the row label to explain what it means. */
  hint?: string
  sparrow:  { verdict: Verdict; note?: string }
  spss:     { verdict: Verdict; note?: string }
  rstata:   { verdict: Verdict; note?: string }
}

const ROWS: Row[] = [
  {
    label: "No installer",
    hint: "Run from a fresh browser tab.",
    sparrow: { verdict: "yes",  note: "Loads a WASM bundle." },
    spss:    { verdict: "no",   note: "Local install required." },
    rstata:  { verdict: "no",   note: "Both require a desktop install." },
  },
  {
    label: "Offline-capable",
    hint: "Works on a laptop without network.",
    sparrow: { verdict: "yes",  note: "Service worker caches the engine." },
    spss:    { verdict: "yes",  note: "Desktop app." },
    rstata:  { verdict: "yes",  note: "Both run locally." },
  },
  {
    label: "Reads .sav natively",
    hint: "Round-trips SPSS files with labels.",
    sparrow: { verdict: "yes",  note: "Importer + writer in the Rust engine." },
    spss:    { verdict: "yes" },
    rstata:  { verdict: "partial", note: "R via `haven`, Stata via foreign tools." },
  },
  {
    label: "Reads .csv and .xlsx",
    sparrow: { verdict: "yes" },
    spss:    { verdict: "yes" },
    rstata:  { verdict: "yes" },
  },
  {
    label: "Point-and-click workflow",
    hint: "Pick a procedure from menus; no syntax required.",
    sparrow: { verdict: "yes" },
    spss:    { verdict: "yes" },
    rstata:  { verdict: "partial", note: "R is code-first; Stata has both." },
  },
  {
    label: "Scriptable / reproducible syntax",
    hint: "Re-run the same analysis from a script.",
    sparrow: { verdict: "partial", note: "Output document is reproducible; full syntax model is roadmap." },
    spss:    { verdict: "yes", note: "SPSS syntax." },
    rstata:  { verdict: "yes", note: "First-class scripting." },
  },
  {
    label: "Your data stays in the browser",
    hint: "No upload of variables to a server.",
    sparrow: { verdict: "yes" },
    spss:    { verdict: "yes",  note: "Desktop, but check enterprise cloud variants." },
    rstata:  { verdict: "yes" },
  },
  {
    label: "Advanced modelling (mixed models, GLMs, survival)",
    hint: "The deep statistical battery beyond descriptives.",
    sparrow: { verdict: "no", note: "Roadmap. Not in the current build." },
    spss:    { verdict: "yes" },
    rstata:  { verdict: "yes", note: "R/Stata's home turf." },
  },
  {
    label: "Charting & APA-style tables",
    sparrow: { verdict: "yes", note: "ECharts-based plots; APA-flavoured tables." },
    spss:    { verdict: "yes" },
    rstata:  { verdict: "partial", note: "Possible, but stitched from packages." },
  },
  {
    label: "Share a result by URL",
    hint: "Send a colleague a link, not a screenshot.",
    sparrow: { verdict: "partial", note: "Capability links for files (#share=); shareable rendered results coming." },
    spss:    { verdict: "no" },
    rstata:  { verdict: "no", note: "Outside of separate report-rendering tooling." },
  },
]

export function WhySparrow() {
  return (
    <div>
      <header className="mb-10 max-w-3xl">
        <h1
          className="font-display text-4xl tracking-tight text-foreground"
          style={{ fontFamily: '"Space Grotesk", Inter, "Helvetica Neue", sans-serif' }}
        >
          Why Sparrow
        </h1>
        <p className="mt-3 text-muted-foreground leading-relaxed">
          Sparrow doesn't replace SPSS or R for everyone — but for the everyday
          descriptives → crosstabs → simple chart workflow, the browser-native
          experience is dramatically lighter. Here's an honest side-by-side.
        </p>
      </header>

      <div className="overflow-x-auto rounded-lg border border-border bg-card">
        <table className="w-full border-collapse min-w-[820px]">
          <thead>
            <tr className="text-left">
              <th className="px-4 py-4 border-b border-border bg-secondary/40 align-bottom">
                <span className="text-xs uppercase tracking-wider text-muted-foreground">Capability</span>
              </th>
              <ColHead title="Sparrow" subtitle="Browser-native" badge="this site" emphasis />
              <ColHead title="SPSS"    subtitle="IBM desktop"     badge="proprietary" />
              <ColHead title="R / Stata" subtitle="Code-first"    badge="install" />
            </tr>
          </thead>
          <tbody>
            {ROWS.map(r => (
              <tr key={r.label} className="border-b border-border/60 last:border-b-0 align-top">
                <td className="px-4 py-4">
                  <div className="font-medium text-foreground">{r.label}</div>
                  {r.hint && (
                    <div className="text-xs text-muted-foreground mt-1">{r.hint}</div>
                  )}
                </td>
                <Cell c={r.sparrow} emphasis />
                <Cell c={r.spss} />
                <Cell c={r.rstata} />
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <section className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-lg border border-border bg-card p-5">
          <h3 className="font-semibold text-foreground">When Sparrow is the right call</h3>
          <ul className="mt-3 text-sm text-muted-foreground space-y-2">
            <li className="flex gap-2"><ChevronRight className="h-4 w-4 text-primary shrink-0 mt-0.5" /> Teaching a stats class and you don't want students licensing software.</li>
            <li className="flex gap-2"><ChevronRight className="h-4 w-4 text-primary shrink-0 mt-0.5" /> Quick descriptives or crosstabs on a CSV someone just sent you.</li>
            <li className="flex gap-2"><ChevronRight className="h-4 w-4 text-primary shrink-0 mt-0.5" /> Opening a legacy .sav on a machine that doesn't have SPSS.</li>
            <li className="flex gap-2"><ChevronRight className="h-4 w-4 text-primary shrink-0 mt-0.5" /> Field work or workshops where install permissions aren't granted.</li>
          </ul>
        </div>
        <div className="rounded-lg border border-border bg-card p-5">
          <h3 className="font-semibold text-foreground">When SPSS or R is a better fit</h3>
          <ul className="mt-3 text-sm text-muted-foreground space-y-2">
            <li className="flex gap-2"><ChevronRight className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" /> You need mixed-effects models, survival analysis, or other advanced procedures.</li>
            <li className="flex gap-2"><ChevronRight className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" /> Your workflow lives in scripted, version-controlled syntax today.</li>
            <li className="flex gap-2"><ChevronRight className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" /> You depend on a specific package ecosystem (lme4, brms, Stata user-written commands).</li>
          </ul>
        </div>
      </section>

      <section className="mt-12">
        <div
          className="relative overflow-hidden rounded-2xl p-8 sm:p-12 text-primary-foreground"
          style={{
            background:
              "linear-gradient(135deg, oklch(0.42 0.21 263) 0%, oklch(0.28 0.18 263) 100%)",
          }}
        >
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
            <div className="max-w-xl">
              <h3
                className="font-display text-2xl sm:text-3xl tracking-tight"
                style={{ fontFamily: '"Space Grotesk", Inter, "Helvetica Neue", sans-serif' }}
              >
                See it on your own file.
              </h3>
              <p className="mt-2 text-primary-foreground/85 text-base">
                The fastest verdict is your own data, dropped into the engine.
              </p>
            </div>
            <a
              href={SPARROW_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-card text-primary font-semibold hover:opacity-90 transition-opacity shadow-md whitespace-nowrap"
            >
              Open Sparrow
              <ArrowUpRight className="h-4 w-4" />
            </a>
          </div>
          <div
            aria-hidden
            className="absolute -right-12 -bottom-12 opacity-25 pointer-events-none text-primary-foreground"
          >
            <Sparrowmark size="xl" iconOnly />
          </div>
        </div>
      </section>

      <p className="mt-8 text-xs text-muted-foreground max-w-3xl">
        Comparison rows reflect publicly visible behaviour as of {new Date().getFullYear()}. SPSS
        and R/Stata are trademarks of their respective owners. If a row here misrepresents
        their behaviour, that's on us — open an issue and we'll fix it.{" "}
        <Link to="/samples" className="text-primary hover:underline">
          Browse the samples
        </Link>{" "}
        if you'd rather try Sparrow on a known dataset first.
      </p>
    </div>
  )
}

function ColHead({
  title, subtitle, badge, emphasis = false,
}: { title: string; subtitle: string; badge: string; emphasis?: boolean }) {
  return (
    <th
      className={
        "px-4 py-4 border-b border-border align-bottom " +
        (emphasis ? "bg-primary/5" : "bg-secondary/40")
      }
    >
      <div className={"font-semibold " + (emphasis ? "text-primary" : "text-foreground")}>
        {title}
      </div>
      <div className="text-xs text-muted-foreground">{subtitle}</div>
      <div className="text-[10px] uppercase tracking-widest mt-1 text-muted-foreground">{badge}</div>
    </th>
  )
}

function Cell({ c, emphasis = false }: { c: Row["sparrow"]; emphasis?: boolean }) {
  return (
    <td className={"px-4 py-4 " + (emphasis ? "bg-primary/5" : "")}>
      <div className="flex items-start gap-2">
        <VerdictIcon v={c.verdict} />
        <div className="text-xs text-muted-foreground leading-relaxed">
          {c.note ?? defaultVerdictText(c.verdict)}
        </div>
      </div>
    </td>
  )
}

function VerdictIcon({ v }: { v: Verdict }) {
  if (v === "yes")
    return (
      <Check className="h-5 w-5 text-accent shrink-0" aria-label="yes" />
    )
  if (v === "no")
    return (
      <X className="h-5 w-5 text-muted-foreground/60 shrink-0" aria-label="no" />
    )
  return (
    <Minus className="h-5 w-5 text-primary shrink-0" aria-label="partial" />
  )
}

function defaultVerdictText(v: Verdict): string {
  if (v === "yes") return "Yes."
  if (v === "no")  return "No."
  return "Partial."
}
